import React from "react";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { ServiceTimePerStop } from "../../../../../models";
import { getServiceTime, updateServiceTime } from "../../../settingsSlice";

import "./ServiceTimeSection.scss";

// The four stop types, in display order. `key` matches the ServiceTimePerStop model.
const FIELDS: Array<{ key: keyof ServiceTimePerStop; label: string }> = [
  { key: "delivery", label: "Delivery" },
  { key: "pickup", label: "Pickup" },
  { key: "dumpYard", label: "Dump Yard" },
  { key: "storage", label: "Storage" },
];

type FormState = Record<keyof ServiceTimePerStop, string>;

const toForm = (v: ServiceTimePerStop): FormState => ({
  delivery: String(v.delivery),
  pickup: String(v.pickup),
  dumpYard: String(v.dumpYard),
  storage: String(v.storage),
});

// Valid = a whole number >= 0 (mirrors the backend rule).
const isValidField = (raw: string): boolean => /^\d+$/.test(raw.trim());

export function ServiceTimeSection() {
  const dispatch = useAppDispatch();
  const serviceTime = useAppSelector((s) => s.settings.serviceTime);
  const loadAPI = useAppSelector((s) => s.settings.getServiceTime);
  const saveAPI = useAppSelector((s) => s.settings.updateServiceTime);

  const [form, setForm] = React.useState<FormState | undefined>(undefined);

  // Load once on mount.
  React.useEffect(() => {
    dispatch(getServiceTime());
  }, [dispatch]);

  // Seed / reset the local form whenever server truth changes
  // (initial load and the post-save refetch).
  React.useEffect(() => {
    if (serviceTime) setForm(toForm(serviceTime));
  }, [serviceTime]);

  // Surface load failures.
  React.useEffect(() => {
    if (!loadAPI.loading && loadAPI.error) {
      toast.error("Failed to load service time. Please try again.");
    }
  }, [loadAPI.loading, loadAPI.error]);

  const setField = (key: keyof ServiceTimePerStop, value: string) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const allValid = form ? FIELDS.every((f) => isValidField(form[f.key])) : false;
  const isDirty =
    !!form &&
    !!serviceTime &&
    FIELDS.some((f) => form[f.key].trim() !== String(serviceTime[f.key]));
  const canSave = allValid && isDirty && !saveAPI.loading;

  const onSave = async () => {
    if (!form || !canSave) return;
    const value: ServiceTimePerStop = {
      delivery: Number(form.delivery),
      pickup: Number(form.pickup),
      dumpYard: Number(form.dumpYard),
      storage: Number(form.storage),
    };
    const result = await dispatch(updateServiceTime(value));
    if (updateServiceTime.fulfilled.match(result)) {
      toast.success("Service time saved.");
    } else {
      toast.error("Failed to save service time. Please try again.");
    }
  };

  const heading = (
    <div className="section-heading">
      <h3>Service Time</h3>
      <span className="section-hint">
        How long a driver spends on-site at each stop type. Used to estimate route
        timing.
      </span>
    </div>
  );

  // Initial load spinner (before we have any values to show).
  if (loadAPI.loading && !form) {
    return (
      <section className="ro-section service-time-section">
        {heading}
        <div className="section-loading">
          <Oval color="#0A60BB" height={28} width={28} secondaryColor="#DAE0E6" />
        </div>
      </section>
    );
  }

  return (
    <section className="ro-section service-time-section">
      {heading}

      <div className="stop-grid">
        {FIELDS.map((f) => {
          const raw = form ? form[f.key] : "";
          const invalid = !isValidField(raw);
          return (
            <div
              key={f.key}
              className={`field-container${invalid ? " has-error" : ""}`}
            >
              <label className="label" htmlFor={`service-time-${f.key}`}>
                {f.label}
              </label>
              <div className="minutes-input">
                <input
                  id={`service-time-${f.key}`}
                  type="number"
                  min={0}
                  step={1}
                  value={raw}
                  aria-label={f.label}
                  aria-invalid={invalid}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
                <span className="suffix">min</span>
              </div>
              {invalid && (
                <span className="form-error">
                  Enter a whole number of minutes (0 or more).
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="ro-footer">
        <button
          type="button"
          className="submit-button"
          onClick={onSave}
          disabled={!canSave}
        >
          {saveAPI.loading ? "Saving…" : "Save changes"}
        </button>
      </div>
    </section>
  );
}
